import React, { useCallback, useState } from 'react';
import { Activity, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST } from '../../utils/constants';
import { useSequentialPolling } from '../../hooks/useSequentialPolling';

/**
 * 任务队列状态栏
 * 显示全局任务队列状态：处理中、排队中、今日完成、预计等待时间
 */
const QueueStatusBar = () => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [metrics, setMetrics] = useState({
    queueSize: 0,
    activeThreads: 0,
    completedTasks: 0,
    poolSize: 0,
    estimatedWaitTime: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = useCallback(async ({ signal } = {}) => {
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/metrics/queue`, {
        signal,
        timeout: 45_000,
      });
      if (signal?.aborted) return;
      setMetrics(data);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      if (signal?.aborted) return;
      console.error('[QueueStatusBar] Failed to fetch metrics:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, []);

  useSequentialPolling(fetchMetrics, 10000);

  // 格式化等待时间
  const formatWaitTime = (seconds) => {
    if (seconds < 60) return `${seconds}秒`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}分钟`;
    const hours = Math.floor(minutes / 60);
    return `${hours}小时${minutes % 60}分钟`;
  };

  // 计算队列压力等级
  const getPressureLevel = () => {
    const { queueSize, poolSize } = metrics;
    if (queueSize === 0) return 'idle';
    if (queueSize < poolSize * 0.5) return 'low';
    if (queueSize < poolSize) return 'medium';
    return 'high';
  };

  const pressureLevel = getPressureLevel();
  const pressureColors = {
    idle: theme.colors.success,
    low: theme.colors.success,
    medium: theme.colors.warning,
    high: theme.colors.error,
  };

  if (error) {
    return (
      <div
        className="px-4 py-2 text-2xs flex items-center gap-2"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderBottom: `1px solid ${theme.colors.borderSecondary}`,
          color: theme.colors.textMuted
        }}
      >
        <Activity size={12} />
        <span>队列状态加载失败</span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="px-4 py-2 text-2xs flex items-center gap-2"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderBottom: `1px solid ${theme.colors.borderSecondary}`,
          color: theme.colors.textMuted
        }}
      >
        <Loader2 size={12} className="animate-spin" />
        <span>加载中...</span>
      </div>
    );
  }

  return (
    <div
      className="px-4 py-2 text-2xs flex items-center gap-4 flex-wrap"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderBottom: `1px solid ${theme.colors.borderSecondary}`
      }}
    >
      {/* 处理中 */}
      <div className="flex items-center gap-1.5">
        <Activity size={12} style={{ color: theme.colors.accentPrimary }} />
        <span style={{ color: theme.colors.textSecondary }}>处理中:</span>
        <span className="font-mono-num font-medium" style={{ color: theme.colors.textPrimary }}>
          {metrics.activeThreads}
        </span>
        <span className="font-mono-num" style={{ color: theme.colors.textMuted }}>/ {metrics.poolSize}</span>
      </div>

      {/* 排队中 */}
      <div className="flex items-center gap-1.5">
        <Clock size={12} style={{ color: pressureColors[pressureLevel] }} />
        <span style={{ color: theme.colors.textSecondary }}>排队:</span>
        <span
          className="font-mono-num font-medium"
          style={{ color: pressureColors[pressureLevel] }}
        >
          {metrics.queueSize}
        </span>
        {metrics.queueSize > 0 && (
          <span className="font-mono-num" style={{ color: theme.colors.textMuted }}>
            (预计 {formatWaitTime(metrics.estimatedWaitTime)})
          </span>
        )}
      </div>

      {/* 今日完成 */}
      <div className="flex items-center gap-1.5">
        <CheckCircle size={12} style={{ color: theme.colors.success }} />
        <span style={{ color: theme.colors.textSecondary }}>今日完成:</span>
        <span className="font-mono-num font-medium" style={{ color: theme.colors.textPrimary }}>
          {metrics.completedTasks}
        </span>
      </div>

      {/* 压力指示器 */}
      {pressureLevel !== 'idle' && (
        <div className="flex items-center gap-1.5 ml-auto animate-in fade-in duration-200">
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: pressureColors[pressureLevel] }}
          />
          <span style={{ color: pressureColors[pressureLevel] }}>
            {pressureLevel === 'low' && '负载正常'}
            {pressureLevel === 'medium' && '负载较高'}
            {pressureLevel === 'high' && '负载很高'}
          </span>
        </div>
      )}
    </div>
  );
};

export default QueueStatusBar;
